import { Injectable } from '@angular/core';
import { ConfigParameter, parametersCompareFn, fetchEsi, oblacDrivesBrandId, fetchOblacDrivesReleases, DeviceParameter } from '@oblac/motion-master-client';
import { BehaviorSubject, catchError, from, lastValueFrom, map, Observable, of, shareReplay, Subject } from 'rxjs';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const ecatmod = require('ecatmod');

@Injectable({
  providedIn: 'root',
})
export class EsiService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  esis$!: Observable<string[]>;

  incompatibleEsiMessage$ = new BehaviorSubject<string>('');

  constructor() {
    this.esis$ = from(fetchOblacDrivesReleases()).pipe(
      map(([releases]) => releases?.find((r) => r.supported === true)?.firmwares ?? []),
      catchError((err) => {
        if (err instanceof Error) {
          console.warn(`Failed to fetch OBLAC Drives releases file: ${err.message}.`);
        }
        return of([]);
      }),
      shareReplay(1)
    );
  }

  async fetchEsi(version: string) {
    version = version.replace(/^v/, '');
    const response = await fetchEsi(oblacDrivesBrandId, version);
    return this.xmlToEsi(response);
  }

  xmlToEsi(text: string) {
    return ecatmod.xmlToEsi(text);
  }

  getParameterFromEsi(esi: any, index: number, subindex: number, productCode: number) {
    return esi.value.descriptions.getParameter(productCode, index, subindex);
  }

  /**
   * Picks the product code to describe parameters with when the caller does not know the device's own.
   *
   * An ESI holds one device entry per product code and the entries are not interchangeable: in the
   * v5.6.6 ESI the Circulo SMM entry describes 805 objects where the Node entry describes 506, and an
   * entry that does not describe an object returns nothing for it. An ESI describing a single device
   * leaves nothing to choose. One describing several does, and 0x201 is the entry this has always read.
   */
  resolveProductCode(esi: any): number {
    const productCodes = esi?.value?.descriptions?.devices?.productCodes as number[] | undefined;

    if (productCodes?.length === 1) {
      return productCodes[0];
    }

    return 0x201;
  }

  async mapEsiToParameters(
    parameters: ConfigParameter[] | DeviceParameter[],
    targetVersion: string,
    productCode?: number
  ) {
    const esiVersions = await lastValueFrom(this.esis$);
    const esiFileVersion = esiVersions.includes(targetVersion ?? '') ? targetVersion : esiVersions[0];
    const esiFile = await this.fetchEsi(esiFileVersion);

    const message =
      esiFileVersion !== targetVersion
        ? `ESI file for firmware ${targetVersion} could not be found. We've mapped the existing parameters with the latest available ESI ${esiFileVersion}.`
        : '';
    this.incompatibleEsiMessage$.next(message);

    const esiProductCode = productCode ?? this.resolveProductCode(esiFile);

    return parameters.map((p) => {
      return { ...p, ...this.getParameterFromEsi(esiFile, p.index, p.subindex, esiProductCode) };
    });
  }

  getAllDeviceParameters(esi: any, productCode: number) {
    const data = esi?.value?.descriptions?.getAllParameters(productCode);
    if (data) {
      data.forEach((d: any) => (d.value = d.defaultData));
      const parameters = data as ConfigParameter[];
      parameters.sort(parametersCompareFn);
      return parameters;
    }
    return;
  }
}
